import { createRef, FC, useState } from "react";
import { Button, Input, Space, Table, TableProps } from "antd";
import type { InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { SvgIcon } from "../../../../common/components/SvgIcon";
import { formatNumber } from "../../../../common/Number";
import { SpendCategory, VendorOrderForm } from "../../../../types/VendorTypes";
import { TaggedResource } from "../../../../types/TagTypes";
import { OrderForm } from "../../../../types/OrderFormTypes";
import { CompanyImageCell } from "../../../../common/components/CompanyImageCell";
import { ScoreTag } from "../../../../common/components/ScoreTag";
import { GenericTable } from "../../../../common/GenericTable";
import classes from "./Vendors.module.less";

const { Column } = Table;

// Build map of subcategory labels to display strings ("Parent > Subcategory")
const buildCategoryDisplayMap = (categories: SpendCategory[]): Map<string, string> => {
    const displayMap = new Map<string, string>();

    for (const category of categories) {
        if (category.children) {
            for (const subCategory of category.children) {
                displayMap.set(subCategory.label, `${category.label} > ${subCategory.label}`);
            }
        }
    }

    return displayMap;
};

// Build map of subcategory labels to sort keys
export const buildCategorySortKeyMap = (categories: SpendCategory[]): Map<string, number> => {
    const MAX_SUBCATEGORIES_PER_PARENT = 1000; // code below assumes no single category contains more than this many child subcategories
    const sortKeyMap = new Map<string, number>();

    for (let catIndex = 0; catIndex < categories.length; catIndex++) {
        const category = categories[catIndex];
        if (category.children) {
            for (let subIndex = 0; subIndex < category.children.length; subIndex++) {
                const subCategory = category.children[subIndex];
                // Combine parent and child indices into a single sortable number
                // e.g., parent 0, child 0 = 0, parent 0, child 1 = 1, parent 1, child 0 = 1000
                sortKeyMap.set(
                    subCategory.label,
                    catIndex * MAX_SUBCATEGORIES_PER_PARENT + subIndex
                );
            }
        }
    }

    return sortKeyMap;
};

// Helper function to convert categories to Ant Design filter tree structure
const buildFilterTree = (categories: SpendCategory[]) => {
    const countSuffix = (category: SpendCategory) =>
        category.count && category.count > 0 ? ` (${category.count})` : "";
    return categories.map((category) => ({
        text: category.label + countSuffix(category),
        value: category.label,
        children: category.children?.map((subCategory) => ({
            text: subCategory.label + countSuffix(subCategory),
            value: subCategory.label,
        })),
    }));
};

export type VendorSorter = {
    columnKey: string;
    order: "ascend" | "descend";
};

// Extended VendorOrderForm with sort keys
export type VendorWithSortKeys = VendorOrderForm & {
    sortContracts: number;
    sortScore: number;
    sortValue: number;
    sortSavings: number;
    sortCategory: number;
};

// Helper to add sort keys to an array of vendors
// Builds the category sort key map once, then applies it to all vendors
export const addSortKeys = (
    vendors: VendorOrderForm[],
    categories?: SpendCategory[]
): VendorWithSortKeys[] => {
    // Build sort key map once for all vendors
    const sortKeyMap = categories ? buildCategorySortKeyMap(categories) : undefined;

    // Get category sort key from map (O(1) lookup)
    const getCategorySortKey = (subCategory: string | undefined): number => {
        if (!subCategory || !sortKeyMap) return Number.MAX_SAFE_INTEGER;
        return sortKeyMap.get(subCategory) ?? Number.MAX_SAFE_INTEGER;
    };

    return vendors.map((vendor) => {
        const hasContracts = (vendor.orderForms?.length ?? 0) > 0;

        return {
            ...vendor,
            // For metrics: vendors with no contracts get MIN_SAFE_INTEGER (less than all other values)
            // This ensures they appear at the end when sorting descending (the default)
            sortContracts: hasContracts ? vendor.metrics.orders : Number.MIN_SAFE_INTEGER,
            sortScore: hasContracts ? vendor.metrics.score || 0 : Number.MIN_SAFE_INTEGER,
            sortValue: hasContracts ? vendor.metrics.value : Number.MIN_SAFE_INTEGER,
            sortSavings: hasContracts
                ? Number(vendor.metrics.savings) || 0
                : Number.MIN_SAFE_INTEGER,
            sortCategory: getCategorySortKey(vendor.subSpendCategory),
        };
    });
};

interface VendorsProps extends TableProps<VendorWithSortKeys> {
    tags?: Map<number, TaggedResource>;
    categories?: SpendCategory[];
    categoryFilter?: string[];
    vendorNameFilter?: string;
    currentSorter?: VendorSorter;
}

const getOrderLink = (row: OrderForm) =>
    row.projectId ? `/project/${row.projectId}` : `/order/${row.id}`;

const expandedRowRender = (vendor: VendorOrderForm) => {
    return (
        <Table
            pagination={false}
            dataSource={vendor.orderForms}
            rowKey="id"
            size="small"
            className={classes.contractTable}
        >
            <Column title="" key="indent" width={56} render={() => null} />
            <Column
                title="Contract"
                dataIndex="name"
                ellipsis={true}
                key="name"
                width={444}
                render={(value: string, row: OrderForm) => {
                    return <Link to={getOrderLink(row)}>{value}</Link>;
                }}
            />
            <Column
                title="Contract score"
                dataIndex="orderScore"
                key="orderScore"
                width={170}
                render={(value: number) => <ScoreTag score={value} type="percentage" />}
            />
            <Column
                title="Contract value"
                dataIndex="value"
                key="value"
                width={170}
                render={(value: number) => formatNumber(value)}
            />
            <Column
                title="Est. savings"
                dataIndex="decrease"
                key="decrease"
                width={160}
                render={(value: number) => formatNumber(value)}
            />
            <Column title="" key="gap" render={() => null} />
        </Table>
    );
};

export const Vendors: FC<VendorsProps> = ({
    tags,
    categories = [],
    categoryFilter,
    vendorNameFilter,
    currentSorter,
    ...restProps
}) => {
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const searchRef = createRef<InputRef>();

    // Build category lookup maps once
    const categoryDisplayMap = buildCategoryDisplayMap(categories);

    const handleNameSearch = async (_selectedKeys: any, confirm: any) => {
        confirm({ closeDropdown: true });
    };

    const handleReset = (clearFilter: any) => {
        clearFilter();
    };

    return (
        <div>
            <GenericTable
                {...restProps}
                rowKey="vendorKey"
                className={classes.table}
                size="small"
                rowClassName={(row) => {
                    if (!expandedRowKeys.length) {
                        return classes.collapsed;
                    }
                    if (expandedRowKeys.includes(row.vendorKey)) {
                        return classes.expanded;
                    }
                    return "";
                }}
                expandable={{
                    expandedRowRender: (row: VendorOrderForm) => expandedRowRender(row),
                    expandedRowKeys,
                    expandIcon: () => null,
                    showExpandColumn: false,
                    expandedRowClassName: () => classes.expandedContent,
                }}
                columns={[
                    {
                        title: "Vendor",
                        dataIndex: "name",
                        key: "name",
                        width: 350,
                        sorter: (a, b) => a.name.localeCompare(b.name),
                        sortDirections: ["ascend"],
                        sortOrder:
                            currentSorter?.columnKey === "name" ? currentSorter.order : undefined,
                        filterIcon: <SearchOutlined />,
                        filteredValue: vendorNameFilter ? [vendorNameFilter] : null,
                        filterDropdown: ({
                            setSelectedKeys,
                            selectedKeys,
                            confirm,
                            clearFilters,
                        }) => {
                            return (
                                <div style={{ padding: 8 }}>
                                    <Input
                                        ref={searchRef}
                                        placeholder="Search vendors"
                                        value={selectedKeys[0]}
                                        onChange={(e) =>
                                            setSelectedKeys(e.target.value ? [e.target.value] : [])
                                        }
                                        onPressEnter={() => {
                                            handleNameSearch(selectedKeys, confirm);
                                        }}
                                        style={{ marginBottom: 8, display: "block" }}
                                    />
                                    <Space>
                                        <Button
                                            type="primary"
                                            onClick={() => handleNameSearch(selectedKeys, confirm)}
                                            icon={<SearchOutlined />}
                                            size="small"
                                            style={{ width: 90 }}
                                        >
                                            Search
                                        </Button>
                                        <Button
                                            onClick={() => handleReset(clearFilters)}
                                            size="small"
                                            style={{ width: 90 }}
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => {
                                                confirm({ closeDropdown: true });
                                            }}
                                        >
                                            Close
                                        </Button>
                                    </Space>
                                </div>
                            );
                        },
                        onFilterDropdownVisibleChange: (visible) => {
                            if (visible) {
                                setTimeout(() => searchRef.current?.focus(), 100);
                            }
                        },
                        render: (value: string, row: VendorOrderForm) => {
                            return (
                                <Space size={4}>
                                    <CompanyImageCell
                                        name={value}
                                        logo={row.hero}
                                        link={`/vendor/${row.vendorKey}`}
                                        nameWrap
                                    />
                                </Space>
                            );
                        },
                    },
                    {
                        title: "Contracts",
                        dataIndex: ["metrics", "orders"],
                        key: "contracts",
                        sorter: (a, b) => a.sortContracts - b.sortContracts,
                        sortDirections: ["descend"],
                        sortOrder:
                            currentSorter?.columnKey === "contracts"
                                ? currentSorter.order
                                : undefined,
                        width: 150,
                        render: (value: number, row: VendorOrderForm) => {
                            const expanded = expandedRowKeys.includes(row.vendorKey);
                            const hasContracts = (row.orderForms?.length ?? 0) > 0;

                            // Return empty cell if no contracts
                            if (!hasContracts) {
                                return null;
                            }

                            return (
                                <Space
                                    size={8}
                                    style={{
                                        cursor: "pointer",
                                        userSelect: "none",
                                        width: "100%",
                                    }}
                                    onClick={() => {
                                        setExpandedRowKeys((pre) =>
                                            expanded
                                                ? pre.filter((key) => key !== row.vendorKey)
                                                : pre.concat(row.vendorKey)
                                        );
                                    }}
                                >
                                    <span>{value}</span>
                                    <SvgIcon
                                        name="down"
                                        size={12}
                                        className={
                                            expanded
                                                ? `${classes.arrow} ${classes["arrow--up"]}`
                                                : classes.arrow
                                        }
                                    />
                                </Space>
                            );
                        },
                    },
                    {
                        title: "Average score",
                        width: 170,
                        dataIndex: ["metrics", "score"],
                        key: "score",
                        sorter: (a, b) => a.sortScore - b.sortScore,
                        sortDirections: ["descend"],
                        sortOrder:
                            currentSorter?.columnKey === "score" ? currentSorter.order : undefined,
                        render: (value: number | undefined, row: VendorOrderForm) => {
                            const hasContracts = (row.orderForms?.length ?? 0) > 0;
                            if (!hasContracts) {
                                return null;
                            }
                            return <ScoreTag score={value} type="percentage" />;
                        },
                    },
                    {
                        title: "Total value",
                        width: 170,
                        dataIndex: ["metrics", "value"],
                        key: "value",
                        sorter: (a, b) => a.sortValue - b.sortValue,
                        sortDirections: ["descend"],
                        sortOrder:
                            currentSorter?.columnKey === "value" ? currentSorter.order : undefined,
                        render: (value: number, row: VendorOrderForm) => {
                            const hasContracts = (row.orderForms?.length ?? 0) > 0;
                            if (!hasContracts) {
                                return null;
                            }
                            return formatNumber(value);
                        },
                    },
                    {
                        title: "Est. savings",
                        width: 160,
                        dataIndex: ["metrics", "savings"],
                        key: "savings",
                        sorter: (a, b) => a.sortSavings - b.sortSavings,
                        sortDirections: ["descend"],
                        sortOrder:
                            currentSorter?.columnKey === "savings"
                                ? currentSorter.order
                                : undefined,
                        render: (value: number | undefined, row: VendorOrderForm) => {
                            const hasContracts = (row.orderForms?.length ?? 0) > 0;
                            if (!hasContracts) {
                                return null;
                            }
                            return formatNumber(Number(value) || 0);
                        },
                    },
                    {
                        title: "Spend category",
                        dataIndex: "subSpendCategory",
                        key: "spendCategory",
                        sorter: (a, b) => a.sortCategory - b.sortCategory,
                        sortDirections: ["ascend"],
                        sortOrder:
                            currentSorter?.columnKey === "spendCategory"
                                ? currentSorter.order
                                : undefined,
                        filters: buildFilterTree(categories),
                        filteredValue: categoryFilter || null,
                        filterMode: "tree" as const,
                        filterSearch: true,
                        render: (value) => {
                            if (!value) return "";
                            return categoryDisplayMap.get(value) || value;
                        },
                    },
                ]}
            />
        </div>
    );
};
